import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import InfoMessage from "src/shared/components/InfoMessage";
import { useAuth } from "src/shared/context/useAuth";
import useGetUnregisteredCases from "../data-access/useGetUnregisteredCases";
import i18n from "src/shared/services/languages/i18n";
import { MRT_ColumnDef, MRT_PaginationState } from "material-react-table";
import { Link } from "react-router-dom";
import useCustomMaterialTable from "src/shared/hooks/useCustomMaterialTable";
import { format, parseISO } from "date-fns";
import getTableLang from "src/shared/services/languages/MaterialReactTable/getTableLang";
import { MessageVariant } from "src/shared/enums/messageTypes/messageTypes";
import useGetBasicFilters from "src/aadeCase/hooks/useGetBasicFilters";
import { FieldValues } from "react-hook-form";
import AadeCaseSearchOrchestrator from "src/aadeCase/components/AadeCaseSearchOrchestrator";

export default function UnregisteredAadeCases() {
  const { t } = useTranslation();

  const { userInfo } = useAuth();

  const [filters, setFilters] = useState<FieldValues>({});
  const [enableSearch, setEnableSearch] = useState<Boolean>(true);
  const [paginationState, setPaginationState] = useState<MRT_PaginationState>({
    pageSize: 10,
    pageIndex: 0,
  });

  const { data, isLoading } = useGetUnregisteredCases({
    filters,
    enabled: enableSearch,
    setEnableSearch,
    paginationState,
  });

  const columns = useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        header: t("Αριθμός υπόθεσης"),
        accessorKey: "aade_case_id",
        accessorFn: (originalRow) => {
          return (
            <Link
              to={`${"/chamber/manageAadeCaseApplication"}/${originalRow.aade_case_id}`}
            >
              {originalRow.aade_case_id}
            </Link>
          );
        },
      },
      {
        header: t("Αριθμός Γ.Ε.ΜΗ."),
        accessorKey: "company_id",
      },
      {
        header: t("ΑΦΜ"),
        accessorKey: "afm",
      },
      {
        header: t("Νομική Μορφή"),
        accessorKey: "legalType",
      },
      {
        header: t("Πλήθος αναντιστοιχιών"),
        accessorKey: "subcase_count",
      },
      {
        header: t("Ημερομηνία Δημιουργίας"),
        accessorKey: "date_created",
        accessorFn: (originalRow) => {
          return (
            <>
              {format(
                parseISO(originalRow.date_created),
                "dd/MM/yyyy HH:mm:ss",
              )}
            </>
          );
        },
      },
    ],
    [i18n.language],
  );

  const basicFilters = useGetBasicFilters();

  const onFilterSuccess = (data: FieldValues) => {
    setFilters({
      ...data,
      date_from: data.date_from ? format(data.date_from, "yyyy-MM-dd") : null,
      date_to: data.date_to ? format(data.date_to, "yyyy-MM-dd") : null,
      legalType: data.legalType ? data.legalType.value : null,
    });
    setEnableSearch(true);
  };

  const table = useCustomMaterialTable({
    columns,
    data: data?.data?.rows ?? [],
    localization: getTableLang(),
    rowCount: data?.data?.rowCount ?? 0,
    onPaginationChange: (updater) => {
      setPaginationState((previous) => updater(previous));
      setEnableSearch(true);
    },
    enableTopToolbar: false,
    enableColumnActions: false,
    enableSorting: false,
    state: {
      isLoading,
      showProgressBars: isLoading,
      pagination: paginationState,
    },
    manualPagination: true,
  });

  return (
    <AadeCaseSearchOrchestrator
      title={`${t("Νέες εισερχόμενες Υποθέσεις Εκκαθάρισης που απευθύνονται στο")} ${userInfo["chamberDescr"]}`}
      infoMessageComponent={
        <InfoMessage
          variant={MessageVariant.Info}
          message={t(
            "Στην οθόνη αυτή εμφανίζονται υποθέσεις που έχουν δημιουργηθεί απο αναντιστοιχία στοιχείων της ΑΑΔΕ με το Γ.Ε.ΜΗ., απευθύνονται στην Υ. Γ.Ε.ΜΗ. και δεν έχουν ακόμη ανατεθεί σε υπάλληλο της Υ. Γ.Ε.ΜΗ. ώστε να αρχίσουν να διεκπεραιώνονται.",
          )}
        />
      }
      filters={basicFilters}
      handleSearch={onFilterSuccess}
      table={table}
    />
  );
}
